import {
    isFeatureEnabled,
    toFeatureState,
} from '@etrigan/feature-toggles-client'
import {
    MatchCenterEvents,
    Product,
    useToggleState,
} from '@news-mono/web-common'
import { FifaWWCMatchData } from '@west-australian-newspapers/publication-types'
import { parse } from 'date-fns'
import format from 'date-fns/format'
import {
    get as getCookie,
    remove as removeCookie,
    set as setCookie,
} from 'js-cookie'
import React, { useEffect, useState } from 'react'
import { AflFixtureData } from '../afl-match-centre/ScoreBoard/types'
import { BauhausSectionHeader } from '../bauhaus/BauhausSectionHeader/BauhausSectionHeader'
import { CollectionRouteProps } from '../collections'
import { FixtureData } from '../cricket-match-centre/ScoreBoard/types'
import { useProduct } from '../__product/useProduct'
import { ThemeMargins } from '../__styling'
import { HideOddsControl } from './HideOddsControl'
import {
    StyledAflRound,
    StyledFifaWWCRound,
    StyledHalfTimeScore,
    StyledMatchCity,
    StyledMatchDate,
    StyledMatchDateDay,
    StyledMatchDateWrapper,
    StyledMatchStadium,
    StyledMatchTournament,
    StyledMatchWinner,
    StyledScoreboardContainer,
    StyledScoreboardContent,
    StyledScoreboardContentAwayTeam,
    StyledScoreboardContentHomeTeam,
    StyledScoreboardContentMatchData,
    StyledScoreBoardWrapper,
    StyledStadiumLocationWrapper,
} from './ScoreBoard.styled'
import { ScoreBoardMoreButton } from './ScoreboardMoreButton'
import { SponsorRow } from './SponsorRow'
import { TeamScore } from './TeamScore'

const HIDE_ODDS_KEY = 'HIDE_ODDS_KEY'

export type ScoreBoardDataProps =
    | { type: 'cricket'; data: FixtureData }
    | { type: 'afl'; data: AflFixtureData }
    | { type: 'fifa-wwc'; data: FifaWWCMatchData }

export interface ScoreBoardProps extends CollectionRouteProps {
    response: ScoreBoardDataProps
    verticalGutters?: [keyof ThemeMargins, keyof ThemeMargins]
    onEvent: (event: MatchCenterEvents) => void
    hideOddsCookieOverride?: boolean
    gameId?: string
}
export const ScoreBoard = ({
    sectionHeader,
    verticalGutters,
    verticalSpacing,
    response,
    onEvent,
    hideOddsCookieOverride,
    gameId,
}: ScoreBoardProps) => {
    const product = useProduct()
    const toggles = useToggleState()
    const [fullDayName, formatedDate] = formatTime(response.data.date)
    const [aflPeriodTime, setAflPeriodTime] = useState('0')
    const isCricket = response.type === 'cricket'
    const isAfl = response.type === 'afl'
    const [hideOdds, setHideOdds] = useState<boolean | undefined>(undefined)

    useEffect(() => {
        const cookie = getCookie(HIDE_ODDS_KEY)
        setHideOdds(!!cookie)
    }, [])

    useEffect(() => {
        if (isAfl) {
            const responseTime = response.data.periodSeconds

            const tickClock = setInterval(() => {
                setAflPeriodTime((prev) => {
                    return prev === '0'
                        ? String(parseInt(responseTime) + 1)
                        : String(parseInt(prev) + 1)
                })
            }, 1000)

            return () => clearInterval(tickClock)
        }
    }, [response.data, isAfl])

    const isPrematch = isCricket && response.data.gameStatus === '0'
    const is7NewsAflBettingOddsToggleEnabled = isFeatureEnabled(
        toFeatureState(toggles),
        '7-news-match-centre-odds-toggle',
    )
    const haveOddsPartner =
        (['perthnow', 'thewest'].includes(product) ||
            (product === 'sevennews' && is7NewsAflBettingOddsToggleEnabled)) &&
        isAfl
    const odds =
        haveOddsPartner && !hideOdds ? response.data.tabTouchOdds : undefined

    //prevent rendering scoreboard until get odds state from cookie for perth now
    if (!hideOddsCookieOverride) {
        if (product === 'perthnow' && hideOdds === undefined) {
            return null
        }
    }

    return (
        <StyledScoreBoardWrapper
            verticalSpacing={verticalSpacing}
            verticalGutters={verticalGutters}
        >
            {sectionHeader?.heading && (
                <BauhausSectionHeader sectionHeader={sectionHeader} />
            )}
            <StyledScoreboardContainer
                isPrematch={isPrematch}
                type={response.type}
            >
                <StyledScoreboardContent>
                    <StyledScoreboardContentHomeTeam>
                        <TeamScore
                            teamData={response}
                            teamSide="home"
                            odds={odds}
                            onEvent={onEvent}
                        />
                    </StyledScoreboardContentHomeTeam>
                    <StyledScoreboardContentMatchData
                        isWWC={response.type === 'fifa-wwc'}
                    >
                        <StyledMatchDateWrapper
                            isRowDisplay={
                                response.type === 'afl' ||
                                response.type === 'fifa-wwc'
                            }
                        >
                            <StyledMatchDateDay>
                                {fullDayName}
                            </StyledMatchDateDay>
                            <StyledMatchDate>{formatedDate}</StyledMatchDate>
                        </StyledMatchDateWrapper>
                        {response.type === 'fifa-wwc' && (
                            <StyledFifaWWCRound>
                                {response.data.matchType}
                            </StyledFifaWWCRound>
                        )}
                        {isCricket && (
                            <StyledMatchWinner>
                                {response.data.result}
                            </StyledMatchWinner>
                        )}
                        <StyledMatchTournament
                            isSmallText={
                                isCricket && response.data.description === ''
                            }
                        >
                            {renderMatchPeriod(response, aflPeriodTime)}
                        </StyledMatchTournament>
                        <StyledStadiumLocationWrapper>
                            {isAfl && (
                                <StyledAflRound>
                                    Round {response.data.round}
                                </StyledAflRound>
                            )}
                            {renderVenueLocation(response)}
                        </StyledStadiumLocationWrapper>
                        {response.type === 'fifa-wwc' &&
                            response.data.period !== 'First Half' &&
                            response.data.period !== 'Pre Match' && (
                                <StyledHalfTimeScore>
                                    HT {response.data.homeTeam.halfScore || 0}-
                                    {response.data.awayTeam.halfScore || 0}
                                </StyledHalfTimeScore>
                            )}
                    </StyledScoreboardContentMatchData>
                    <StyledScoreboardContentAwayTeam>
                        <TeamScore
                            teamData={response}
                            teamSide="away"
                            odds={odds}
                            onEvent={onEvent}
                        />
                    </StyledScoreboardContentAwayTeam>
                </StyledScoreboardContent>

                {product === Product.SevenNews && response.type !== 'afl' && (
                    <ScoreBoardMoreButton
                        type={response.type}
                        gameId={gameId}
                    />
                )}

                {product === Product.SevenNews &&
                    response.type === 'afl' &&
                    (hideOdds || !is7NewsAflBettingOddsToggleEnabled) && (
                        <ScoreBoardMoreButton
                            type={response.type}
                            gameId={gameId}
                        />
                    )}

                {odds && <SponsorRow odds={odds} onEvent={onEvent} />}
            </StyledScoreboardContainer>

            {haveOddsPartner && (
                <HideOddsControl
                    value={!hideOdds}
                    onToggle={() => {
                        const newValue = !hideOdds
                        newValue
                            ? setCookie(HIDE_ODDS_KEY, 'true')
                            : removeCookie(HIDE_ODDS_KEY)
                        setHideOdds(newValue)
                    }}
                />
            )}
        </StyledScoreBoardWrapper>
    )
}

const getRawDateFromString = (dateFromApi: string) => {
    if (dateFromApi.includes('T')) {
        // cricket
        return new Date(dateFromApi)
    } else {
        // football
        return parse(dateFromApi, 'yyyy-MM-dd HH:mm:ss', new Date())
    }
}

const formatTime = (utcTime: string) => {
    const rawDate = getRawDateFromString(utcTime)
    const fullDayName = rawDate.toLocaleString('en-us', { weekday: 'long' })
    const formatedDate = format(rawDate, 'd MMM p')

    return [fullDayName, formatedDate]
}

const renderMatchPeriod = (
    props: ScoreBoardDataProps,
    aflPeriodSeconds?: string,
) => {
    switch (props.type) {
        case 'cricket':
            return props.data.description || props.data.competitionName

        case 'afl':
            if (
                props.data.match_status === 'complete' ||
                props.data.match_status === 'postmatch'
            ) {
                return 'Full time'
            }
            if (
                props.data.match_status === 'prematch' ||
                props.data.match_status === 'scheduled'
            ) {
                return 'PRE-GAME'
            }
            if (props.data.match_status === 'playing') {
                if (
                    (props.data.period === '1' &&
                        props.data.periodCompleted === '1') ||
                    (props.data.period === '3' &&
                        props.data.periodCompleted === '3')
                ) {
                    return `QTR TIME`
                }
                if (
                    props.data.period === '2' &&
                    props.data.periodCompleted === '2'
                ) {
                    return 'HALF TIME'
                }
                if (
                    props.data.period === '4' &&
                    props.data.periodCompleted === '4'
                ) {
                    return 'FULL TIME'
                }

                return (
                    <>
                        {`QTR ${props.data.period}`}
                        <span>
                            {aflPeriodSeconds &&
                                secondsToTime(parseInt(aflPeriodSeconds))}
                        </span>
                    </>
                )
            }
            return props.data.match_status
        case 'fifa-wwc':
            return props.data.period
        default:
            return ''
    }
}
const renderVenueLocation = (props: ScoreBoardDataProps) => {
    switch (props.type) {
        case 'cricket': {
            return (
                <>
                    <StyledMatchStadium>
                        {props.data.venue.name}
                    </StyledMatchStadium>
                    <StyledMatchCity>
                        {props.data.venue.city} {props.data.venue.country}
                    </StyledMatchCity>
                </>
            )
        }
        case 'afl': {
            return (
                <StyledMatchStadium>{props.data.venue.name}</StyledMatchStadium>
            )
        }
        case 'fifa-wwc': {
            return (
                <StyledMatchStadium isWWC={true}>
                    {props.data.venue.name}
                </StyledMatchStadium>
            )
        }
        default: {
            return ''
        }
    }
}

export const secondsToTime = (seconds: number) => {
    const m = Math.floor((seconds % 3600) / 60)
            .toString()
            .padStart(2, '0'),
        s = Math.floor(seconds % 60)
            .toString()
            .padStart(2, '0')

    return m + ':' + s
}
